//src/contexts/AuthContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { AccessScope, AuthUser, LoginRequest } from "@/types";
import {
  api,
  authToken,
  clearTokens,
  forgotPasswordRequest,
  getAccessToken,
  resetPasswordRequest,
  selectAuthContext,
  type AuthContextOption,
} from "@/lib/apiClient";

const CONTEXT_TOKEN_KEY = "normex:pending-context-token";
const CONTEXT_OPTIONS_KEY = "normex:pending-context-options";

type MeGroup = { id: string; name: string; description?: string | null };
type MeTenant = {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  tenant_legal_name?: string | null;
  tenant_document?: string | null;
  is_active: boolean;
};
type MeUnit = {
  unit_id: string;
  tenant_id: string;
  unit_name: string;
  unit_legal_name?: string | null;
  unit_document_number?: string | null;
  is_active: boolean;
} | null;
type MeContacts = { email?: string | null; phone?: string | null; whatsapp?: string | null };
type MeResponse = {
  user_id: string;
  person_id: string;
  is_active: boolean;
  full_name: string;
  login: string;
  contacts: MeContacts;
  roles: string[];
  groups: MeGroup[];
  permissions: string[];
  access_scope?: AccessScope | null;
  context_type?: "platform" | "tenant" | "client" | null;
  token_context_type?: "platform" | "tenant" | "client" | null;
  tenant: MeTenant | null;
  unit: MeUnit;
};

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isContextSwitching: boolean;
  isContextSelectionRequired: boolean;
  availableContexts: AuthContextOption[];
  login: (data: LoginRequest) => Promise<{ contextSelectionRequired: boolean }>;
  selectContext: (contextKey: string) => Promise<void>;
  cancelContextSelection: () => void;
  logout: () => void;
  refreshMe: () => Promise<void>;
  runContextSwitch: <T>(operation: () => Promise<T>) => Promise<T>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}

export function usePermission(permission: string): boolean {
  const { user } = useAuth();
  return user?.permissions?.includes(permission) ?? false;
}

export function useAnyPermission(permissions: string[]): boolean {
  const { user } = useAuth();
  return !!user && permissions.some((permission) => user.permissions?.includes(permission));
}

function mapMeToAuthUser(data: MeResponse): AuthUser {
  const permissions = Array.isArray(data.permissions) ? data.permissions : [];
  const accessScope: AccessScope =
    data.access_scope === "platform" || permissions.some((permission) => permission.startsWith("platform."))
      ? "platform"
      : "tenant";
  return {
    id: data.user_id,
    person_id: data.person_id,
    tenant_id: data.tenant?.tenant_id || "",
    access_scope: accessScope,
    context_type: data.context_type || (accessScope === "platform" ? "platform" : "tenant"),
    token_context_type: data.token_context_type || data.context_type || (accessScope === "platform" ? "platform" : "tenant"),
    tenant: data.tenant
      ? {
          id: data.tenant.tenant_id,
          name: data.tenant.tenant_name,
          slug: data.tenant.tenant_slug,
          domain: undefined,
          logo_url: undefined,
          primary_color: undefined,
          accent_color: undefined,
          timezone: "America/Sao_Paulo",
          locale: "pt-BR",
          is_active: data.tenant.is_active,
          created_at: "",
          updated_at: "",
        }
      : null,
    email: data.contacts?.email || data.login,
    login: data.login,
    name: data.full_name,
    avatar_url: undefined,
    permissions,
    groups: Array.isArray(data.groups)
      ? data.groups.map((group) => ({
          id: group.id,
          tenant_id: data.tenant?.tenant_id || "",
          name: group.name,
          description: group.description || undefined,
          is_system: false,
          created_at: "",
        }))
      : [],
    roles: Array.isArray(data.roles) ? data.roles : [],
    unit: data.unit
      ? {
          id: data.unit.unit_id,
          tenant_id: data.unit.tenant_id,
          name: data.unit.unit_name,
          legal_name: data.unit.unit_legal_name || undefined,
          document_number: data.unit.unit_document_number || undefined,
          is_active: data.unit.is_active,
        }
      : null,
    phone: data.contacts?.phone || null,
    whatsapp: data.contacts?.whatsapp || null,
    is_active: data.is_active,
    contacts: {
      email: data.contacts?.email || null,
      phone: data.contacts?.phone || null,
      whatsapp: data.contacts?.whatsapp || null,
    },
  };
}

async function fetchMe(): Promise<AuthUser> {
  return mapMeToAuthUser(await api.get<MeResponse>("/me"));
}

function readPendingContexts(): AuthContextOption[] {
  try {
    return JSON.parse(sessionStorage.getItem(CONTEXT_OPTIONS_KEY) || "[]") as AuthContextOption[];
  } catch {
    return [];
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isContextSwitching, setIsContextSwitching] = useState(false);
  const contextSwitchDepthRef = useRef(0);
  const [contextToken, setContextToken] = useState<string | null>(() => sessionStorage.getItem(CONTEXT_TOKEN_KEY));
  const [availableContexts, setAvailableContexts] = useState<AuthContextOption[]>(readPendingContexts);

  const clearPendingContext = useCallback(() => {
    setContextToken(null);
    setAvailableContexts([]);
    sessionStorage.removeItem(CONTEXT_TOKEN_KEY);
    sessionStorage.removeItem(CONTEXT_OPTIONS_KEY);
  }, []);

  const refreshMe = useCallback(async () => setUser(await fetchMe()), []);

  const runContextSwitch = useCallback(async <T,>(operation: () => Promise<T>): Promise<T> => {
    contextSwitchDepthRef.current += 1;
    setIsContextSwitching(true);

    // Entrega um ciclo ao React antes de alterar storage/contexto. Assim os guards
    // suspendem a avaliação de permissões antes que o /me passe de Tenant <-> Client.
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));

    try {
      return await operation();
    } finally {
      contextSwitchDepthRef.current = Math.max(0, contextSwitchDepthRef.current - 1);
      if (contextSwitchDepthRef.current === 0) {
        setIsContextSwitching(false);
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!getAccessToken()) {
        if (mounted) setIsLoading(false);
        return;
      }
      try {
        const me = await fetchMe();
        if (mounted) setUser(me);
      } catch {
        clearTokens();
        if (mounted) setUser(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    setIsLoading(true);
    try {
      const response = await authToken({ email: data.email.trim(), password: data.password });
      if (response.context_selection_required) {
        const token = response.context_token || "";
        setContextToken(token);
        setAvailableContexts(response.contexts || []);
        sessionStorage.setItem(CONTEXT_TOKEN_KEY, token);
        sessionStorage.setItem(CONTEXT_OPTIONS_KEY, JSON.stringify(response.contexts || []));
        setUser(null);
        return { contextSelectionRequired: true };
      }
      clearPendingContext();
      setUser(await fetchMe());
      return { contextSelectionRequired: false };
    } catch (error) {
      clearTokens();
      clearPendingContext();
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearPendingContext]);

  const selectContext = useCallback(async (contextKey: string) => {
    if (!contextToken) throw new Error("Seleção de acesso expirada. Faça login novamente.");
    setIsLoading(true);
    try {
      await selectAuthContext(contextToken, contextKey);
      clearPendingContext();
      setUser(await fetchMe());
    } catch (error) {
      clearTokens();
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearPendingContext, contextToken]);

  const cancelContextSelection = useCallback(() => {
    clearTokens();
    clearPendingContext();
    setUser(null);
  }, [clearPendingContext]);

  const logout = useCallback(() => {
    clearTokens();
    clearPendingContext();
    setUser(null);
  }, [clearPendingContext]);

  const forgotPassword = useCallback(async (email: string) => {
    await forgotPasswordRequest(email.trim());
  }, []);
  const resetPassword = useCallback(async (token: string, password: string) => {
    await resetPasswordRequest(token, password);
    clearTokens();
    clearPendingContext();
    setUser(null);
  }, [clearPendingContext]);

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    isContextSwitching,
    isContextSelectionRequired: !!contextToken && availableContexts.length > 0,
    availableContexts,
    login,
    selectContext,
    cancelContextSelection,
    logout,
    refreshMe,
    runContextSwitch,
    forgotPassword,
    resetPassword,
  }), [
    user,
    isLoading,
    isContextSwitching,
    contextToken,
    availableContexts,
    login,
    selectContext,
    cancelContextSelection,
    logout,
    refreshMe,
    runContextSwitch,
    forgotPassword,
    resetPassword,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
